import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id);
      else setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user.id);
      else { setProfile(null); setLoading(false); }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function fetchProfile(userId) {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', userId)
      .single();
    setProfile(data);
    setLoading(false);
  }

  async function updateProfile(updates) {
    if (!user) return;
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('user_id', user.id)
      .select()
      .single();
    if (!error) setProfile(data);
    return { data, error };
  }

  async function signIn(email, password) {
    return supabase.auth.signInWithPassword({ email, password });
  }

  async function signUp(email, password, name, firm, inviteCode) {
    // Validate invite code first
    const { data: invite, error: inviteError } = await supabase
      .from('invites')
      .select('*')
      .eq('code', inviteCode.trim().toUpperCase())
      .eq('used', false)
      .single();

    if (inviteError || !invite) {
      return { error: { message: 'Invalid or already-used invite code. Contact Liz to get access.' } };
    }

    // Check expiry
    if (invite.expires_at && new Date(invite.expires_at) < new Date()) {
      return { error: { message: 'This invite code has expired. Contact Liz for a new one.' } };
    }

    // Create the auth user
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) return { error };

    const userId = data.user?.id;
    if (!userId) return { error: { message: 'Signup failed. Please try again.' } };

    // Mark invite as used
    await supabase
      .from('invites')
      .update({ used: true, used_by: userId, used_at: new Date().toISOString() })
      .eq('id', invite.id);

    // Create profile — 'name' matches what the app reads
    await supabase.from('profiles').upsert({
      user_id: userId,
      name: name,
      firm_name: firm,
      email: email,
      annual_goal: 500000,
      monthly_target: 41667,
      new_clients_per_month: 2,
      proposals_per_month: 5,
      discovery_calls_per_month: 10,
      ipas_per_week: 8,
      meetings_per_month: 6,
      is_admin: false,
    });

    return { data, error: null };
  }

  async function signOut() {
    return supabase.auth.signOut();
  }

  // Admin: generate an invite code (only works if user is admin)
  async function generateInvite(label = '', expiresInDays = 30) {
    const code = Math.random().toString(36).substring(2, 8).toUpperCase() +
                 Math.random().toString(36).substring(2, 6).toUpperCase();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + expiresInDays);

    const { data, error } = await supabase
      .from('invites')
      .insert({
        code,
        label,
        created_by: user?.id,
        expires_at: expiresAt.toISOString(),
        used: false,
      })
      .select()
      .single();

    return { data, error };
  }

  async function getInvites() {
    const { data, error } = await supabase
      .from('invites')
      .select('*')
      .order('created_at', { ascending: false });
    return { data, error };
  }

  async function deleteInvite(id) {
    return supabase.from('invites').delete().eq('id', id);
  }

  return (
    <AuthContext.Provider value={{
      user, profile, loading,
      signIn, signUp, signOut,
      updateProfile, fetchProfile,
      generateInvite, getInvites, deleteInvite
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
